import React, { useState, useRef } from 'react';
import {
  View,
  Text,
  Animated,
  PanResponder,
  Dimensions,
  StyleSheet,
} from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';

const { width: screenWidth } = Dimensions.get('window');

interface ExpandableToastProps {
  type: 'success' | 'error' | 'info';
  title: string;
  message: string;
  fullMessage?: string;
  isExpandable: boolean;
  onHide?: () => void;
}

const ExpandableToast: React.FC<ExpandableToastProps> = ({
  type,
  title,
  message,
  fullMessage,
  isExpandable,
  onHide,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const heightAnim = useRef(new Animated.Value(60)).current;
  const opacityAnim = useRef(new Animated.Value(1)).current;
  const pullIndicatorAnim = useRef(new Animated.Value(0)).current;
  const translateYAnim = useRef(new Animated.Value(0)).current;

  const panResponder = useRef(
    PanResponder.create({
      onMoveShouldSetPanResponder: (_, gestureState) => {
        // Respond to vertical gestures - lowered threshold for better "grab" feel
        return Math.abs(gestureState.dy) > Math.abs(gestureState.dx) && Math.abs(gestureState.dy) > 2;
      },
      onPanResponderGrant: () => {
        // Show pull indicator animation for expandable toasts
        if (isExpandable) {
          Animated.spring(pullIndicatorAnim, {
            toValue: 1,
            useNativeDriver: false,
          }).start();
        }
      },
      onPanResponderMove: (_, gestureState) => {
        // Handle swipe up to dismiss (ALWAYS AVAILABLE - top priority) - made less sensitive
        if (gestureState.dy < -25) {
          const rawDismissProgress = Math.abs(gestureState.dy) / 80;
          const dismissProgress = Math.min(rawDismissProgress, 1);
          
          // Rubber band effect - stronger resistance after threshold
          let translateValue;
          if (rawDismissProgress <= 1) {
            translateValue = gestureState.dy; // Direct movement up to threshold
          } else {
            // Rubber band: slower movement beyond threshold
            const excess = rawDismissProgress - 1;
            const rubberBandEffect = Math.log(excess + 1) * 20; // Logarithmic resistance
            translateValue = -80 - rubberBandEffect;
          }
          
          translateYAnim.setValue(translateValue);
          opacityAnim.setValue(Math.max(1 - dismissProgress * 0.6, 0.2)); // Don't fade completely
          
          // Reset expand/collapse animations when dismissing
          if (isExpandable) {
            heightAnim.setValue(isExpanded ? 200 : 60);
            pullIndicatorAnim.setValue(isExpanded ? 1 : 0);
          }
        }
        // Handle downward pulls to expand (only for expandable toasts)
        else if (isExpandable && gestureState.dy > 15 && !isExpanded) {
          // Reset dismiss animations when expanding
          translateYAnim.setValue(0);
          opacityAnim.setValue(1);
          
          const rawProgress = gestureState.dy / 50;
          const progress = Math.min(rawProgress, 1);
          
          // Rubber band effect for downward pulls
          let heightValue;
          if (rawProgress <= 1) {
            heightValue = 60 + (140 * progress); // Smooth expansion to 200px
          } else {
            // Rubber band: slower expansion beyond target
            const excess = rawProgress - 1;
            const rubberBandEffect = Math.log(excess + 1) * 30;
            heightValue = 200 + rubberBandEffect;
          }
          
          heightAnim.setValue(heightValue);
          pullIndicatorAnim.setValue(progress);
        }
        // Handle upward pulls to collapse when expanded (but not strong enough to dismiss)
        else if (isExpandable && gestureState.dy < -5 && gestureState.dy > -25 && isExpanded) {
          // Reset dismiss animations when collapsing
          translateYAnim.setValue(0);
          opacityAnim.setValue(1);
          
          const rawProgress = Math.abs(gestureState.dy) / 50;
          const progress = Math.min(rawProgress, 1);
          
          // Rubber band effect for upward pulls when expanded
          let heightValue;
          if (rawProgress <= 1) {
            heightValue = 200 - (140 * progress); // Smooth collapse to 60px
          } else {
            // Rubber band: resistance when trying to collapse too much
            const excess = rawProgress - 1;
            const rubberBandEffect = Math.log(excess + 1) * 20;
            heightValue = Math.max(60 - rubberBandEffect, 40); // Don't go below 40px
          }
          
          heightAnim.setValue(heightValue);
          pullIndicatorAnim.setValue(1 - progress);
        }
        // Handle small movements - subtle rubber band "grab" effect (expanded range)
        else {
          // Apply resistance for a natural rubber band feel
          const resistance = 0.4; // Allow 40% of the movement through
          let rubberBandValue;
          
          // More responsive rubber banding based on movement strength - expanded range
          if (Math.abs(gestureState.dy) <= 15) {
            // Small movements - direct response (expanded from 10px to 15px)
            rubberBandValue = gestureState.dy * resistance;
          } else {
            // Medium movements - add slight resistance
            const direction = gestureState.dy > 0 ? 1 : -1;
            const magnitude = Math.abs(gestureState.dy);
            const baseMovement = 15 * resistance; // Updated base
            const excessMovement = (magnitude - 15) * 0.2; // Strong resistance for excess
            rubberBandValue = direction * (baseMovement + excessMovement);
          }
          
          translateYAnim.setValue(rubberBandValue);
          
          // Add subtle opacity change for very responsive feedback
          const opacityChange = Math.min(Math.abs(gestureState.dy) / 150, 0.05);
          opacityAnim.setValue(1 - opacityChange);
          
          // Reset expand/collapse animations to current state
          if (isExpandable) {
            heightAnim.setValue(isExpanded ? 200 : 60);
            pullIndicatorAnim.setValue(isExpanded ? 1 : 0);
          }
        }
      },
      onPanResponderRelease: (_, gestureState) => {
        // Handle swipe up to dismiss (threshold: -60px upward)
        if (gestureState.dy < -60) {
          dismissToast();
          return;
        }

        // Handle expand/collapse for expandable toasts
        if (isExpandable) {
          // Expand if pulled down enough and not expanded
          if (gestureState.dy > 30 && !isExpanded) {
            expandToast();
          }
          // Collapse if pulled up enough and expanded (but not enough to dismiss)
          else if (gestureState.dy < -30 && isExpanded) {
            collapseToast();
          }
          // Spring back to current state if gesture wasn't strong enough
          else {
            Animated.parallel([
              Animated.spring(heightAnim, {
                toValue: isExpanded ? 200 : 60,
                useNativeDriver: false,
                tension: 150,
                friction: 8,
              }),
              Animated.spring(pullIndicatorAnim, {
                toValue: isExpanded ? 1 : 0,
                useNativeDriver: false,
                tension: 150,
                friction: 8,
              }),
            ]).start();
          }
        }

        // Always reset dismiss animations with nice spring
        Animated.parallel([
          Animated.spring(translateYAnim, {
            toValue: 0,
            useNativeDriver: false,
            tension: 150,
            friction: 8,
          }),
          Animated.spring(opacityAnim, {
            toValue: 1,
            useNativeDriver: false,
            tension: 150,
            friction: 8,
          }),
        ]).start();
      },
    })
  ).current;

  const expandToast = () => {
    setIsExpanded(true);
    Animated.parallel([
      Animated.spring(heightAnim, {
        toValue: 200, // Expanded height
        useNativeDriver: false,
        tension: 120,
        friction: 8,
      }),
      Animated.spring(pullIndicatorAnim, {
        toValue: 1,
        useNativeDriver: false,
        tension: 120,
        friction: 8,
      }),
    ]).start();
  };

  const collapseToast = () => {
    setIsExpanded(false);
    Animated.parallel([
      Animated.spring(heightAnim, {
        toValue: 60, // Collapsed height
        useNativeDriver: false,
        tension: 120,
        friction: 8,
      }),
      Animated.spring(pullIndicatorAnim, {
        toValue: 0,
        useNativeDriver: false,
        tension: 120,
        friction: 8,
      }),
    ]).start();
  };

  const dismissToast = () => {
    Animated.parallel([
      Animated.timing(translateYAnim, {
        toValue: -100,
        duration: 250,
        useNativeDriver: false, // Changed to false to match other animations
      }),
      Animated.timing(opacityAnim, {
        toValue: 0,
        duration: 250,
        useNativeDriver: false, // Changed to false to match other animations
      }),
    ]).start(() => {
      if (onHide) {
        onHide();
      }
    });
  };

  const getBorderColor = () => {
    switch (type) {
      case 'success': return '#51B868';
      case 'error': return '#F97777';
      case 'info': return '#39BEB7';
      default: return '#F97777';
    }
  };

  const getIcon = () => {
    switch (type) {
      case 'success': return 'check-circle';
      case 'error': return 'error';
      case 'info': return 'info';
      default: return 'error';
    }
  };

  return (
    <Animated.View
      {...panResponder.panHandlers}
      style={[
        styles.container,
        {
          height: heightAnim,
          borderLeftColor: getBorderColor(),
          opacity: opacityAnim,
          transform: [{ translateY: translateYAnim }],
        },
      ]}
    >
      {/* Header with icon and title */}
      <View style={styles.header}>
        <MaterialIcons 
          name={getIcon()} 
          size={20} 
          color={getBorderColor()} 
          style={styles.icon}
        />
        <Text style={styles.title} numberOfLines={isExpanded ? undefined : 1}>
          {title}
        </Text>
        {isExpandable && (
          <View style={styles.pullIndicatorContainer}>
            <Animated.View
              style={[
                styles.pullIndicator,
                {
                  transform: [{
                    rotate: pullIndicatorAnim.interpolate({
                      inputRange: [0, 1],
                      outputRange: ['0deg', '180deg'],
                    }),
                  }],
                },
              ]}
            >
              <MaterialIcons 
                name="keyboard-arrow-down" 
                size={16} 
                color="#8D8E90" 
              />
            </Animated.View>
          </View>
        )}
      </View>

      {/* Message content */}
      <View style={styles.content}>
        <Text 
          style={styles.message}
          numberOfLines={isExpanded ? undefined : 2}
        >
          {isExpanded ? (fullMessage || message) : message}
        </Text>
        
        {isExpandable && !isExpanded && (
          <Text style={styles.hint}>Pull down for details • Swipe up to dismiss</Text>
        )}
        {!isExpandable && (
          <Text style={styles.hint}>Swipe up to dismiss</Text>
        )}
      </View>

      {/* Pull indicator line */}
      {isExpandable && (
        <View style={styles.pullLine}>
          <Animated.View
            style={[
              styles.pullLineIndicator,
              {
                backgroundColor: getBorderColor(),
                opacity: pullIndicatorAnim.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0.3, 0.8],
                }),
              },
            ]}
          />
        </View>
      )}
    </Animated.View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: '#23262B',
    borderLeftWidth: 4,
    borderRadius: 8,
    marginVertical: 5,
    paddingHorizontal: 15,
    paddingVertical: 10,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
    minHeight: 60,
    width: Math.min(screenWidth - 40, 400), // Fixed width: screen width minus 40px padding, max 400px
    alignSelf: 'center',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 5,
  },
  icon: {
    marginRight: 8,
  },
  title: {
    flex: 1,
    fontSize: 14,
    fontWeight: '600',
    color: '#FFFFFF',
    lineHeight: 18,
  },
  pullIndicatorContainer: {
    marginLeft: 8,
  },
  pullIndicator: {
    alignItems: 'center',
    justifyContent: 'center',
  },
  content: {
    flex: 1,
  },
  message: {
    fontSize: 12,
    color: '#8D8E90',
    lineHeight: 16,
    flexWrap: 'wrap',
  },
  hint: {
    fontSize: 10,
    color: '#6B7280',
    fontStyle: 'italic',
    marginTop: 4,
    textAlign: 'center',
  },
  pullLine: {
    position: 'absolute',
    bottom: 3,
    left: '50%',
    marginLeft: -15,
    width: 30,
    height: 2,
    alignItems: 'center',
    justifyContent: 'center',
  },
  pullLineIndicator: {
    width: '100%',
    height: 2,
    borderRadius: 1,
  },
});

export default ExpandableToast;
